/**
 * Select mode for the Message Requests list (#3039).
 *
 * The mode is one attribute on the overlay (data-select-mode) and one per row
 * (data-selected). Rows style themselves off those, so a keyset page appended
 * while the mode is active already renders selectable and no pass over the new
 * rows is needed for appearance. Two things still need a per-row pass: the
 * shared swipe module matches on an attribute, and the row is a button only
 * while the mode is on.
 *
 * Drives the list through window.PmRequests (pm/requests.js).
 */
(function () {
  'use strict';

  var active = false;
  var selected = Object.create(null);

  // Captured when the mode opens, because the header shows a selection count in
  // their place and has to read as it did on exit.
  var restingSelectLabel = '';
  var restingDeleteLabel = '';

  function api() {
    return window.PmRequests || null;
  }

  function el(id) {
    return document.getElementById(id);
  }

  /**
   * The selected ids that still have a row, pruning the rest as it reads.
   * An accept or a block on another device takes a row out from under the
   * mode, and a left-behind id is declined and counted as if it were there.
   */
  function selectedIds() {
    var a = api();
    var ids = Object.keys(selected);
    if (!a || typeof a.row !== 'function') return ids;
    var live = [];
    for (var i = 0; i < ids.length; i++) {
      if (a.row(ids[i])) {
        live.push(ids[i]);
      } else {
        delete selected[ids[i]];
      }
    }
    return live;
  }

  /** The row's own control: opens the conversation, or toggles the selection. */
  function rowButton(row) {
    return row.querySelector('.message-request-open');
  }

  /** Put a row back to how it reads outside select mode. */
  function resetRow(row) {
    row.setAttribute('data-swipe-row', '');
    row.removeAttribute('data-selected');
    var btn = rowButton(row);
    if (btn) btn.removeAttribute('aria-pressed');
  }

  /**
   * Match one row to the current mode: swipe off, pressed state, selection.
   *
   * aria-pressed goes on the row's button, never on the row: the row is the
   * listitem its list needs, and a role on it would make the button
   * presentational.
   */
  function applyModeToRow(row) {
    if (!active) {
      resetRow(row);
      return;
    }
    row.removeAttribute('data-swipe-row');
    var id = row.getAttribute('data-thread-id');
    var isSelected = !!(id && selected[id]);
    var btn = rowButton(row);
    if (btn) btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    if (isSelected) {
      row.setAttribute('data-selected', 'true');
    } else {
      row.removeAttribute('data-selected');
    }
  }

  function applyModeToRows() {
    var a = api();
    if (!a) return;
    var rows = a.rows();
    for (var i = 0; i < rows.length; i++) applyModeToRow(rows[i]);
  }

  function updateChrome() {
    var a = api();
    if (!a) return;

    var count = selectedIds().length;
    var selectBtn = el('message-requests-view-select');
    var deleteBtn = el('message-requests-view-delete-selected');
    var deleteLabel = el('message-requests-view-delete-selected-label');

    if (!active) {
      if (selectBtn) selectBtn.textContent = restingSelectLabel;
      if (deleteBtn) deleteBtn.disabled = true;
      if (deleteLabel) deleteLabel.textContent = restingDeleteLabel;
      // Re-applies the count line and the Select button's own visibility.
      a.setCount(a.getCount());
      return;
    }

    if (selectBtn) {
      selectBtn.textContent = window.tOr('common.done', 'Done');
      selectBtn.classList.remove('hidden');
    }
    // The count line is the one live region in the header, so the selection
    // count is announced there. The heading stays put: a heading that renames
    // itself moves under the user in the heading list.
    var countLine = el('message-requests-view-count');
    if (countLine) {
      // Unhidden first: a live region announces nothing while it is display
      // none, and revealing it afterwards does not replay the text.
      countLine.classList.remove('hidden');
      countLine.textContent = window.tOr('common.pm.requests_selected_count', count + ' selected', { count: count });
    }

    if (deleteBtn) deleteBtn.disabled = count === 0;
    if (deleteLabel) {
      deleteLabel.textContent = count === 0
        ? restingDeleteLabel
        : window.tOr('common.pm.requests_delete_selected', 'Delete ' + count, { count: count });
    }
  }

  // Select mode holds the rendered list still: a refresh that replaced it would
  // discard the selection. pm/requests-live.js waits for the mode to end.
  function announceMode() {
    try {
      document.dispatchEvent(new CustomEvent('pm:requests-select-changed', {
        detail: { active: active }
      }));
    } catch (e) { /* CustomEvent unavailable: nothing downstream is required */ }
  }

  function enter() {
    var a = api();
    var inner = a && a.inner();
    if (active || !inner) return;

    // A delete still inside its undo window is committed rather than carried
    // into a mode where its row cannot be shown again.
    if (window.PmRequestsUndo) window.PmRequestsUndo.commitNow();
    if (window.SwipeActions) window.SwipeActions.closeAll();

    var selectBtn = el('message-requests-view-select');
    var deleteLabel = el('message-requests-view-delete-selected-label');
    restingSelectLabel = selectBtn ? selectBtn.textContent.trim() : '';
    restingDeleteLabel = deleteLabel ? deleteLabel.textContent.trim() : '';

    active = true;
    selected = Object.create(null);
    inner.setAttribute('data-select-mode', 'on');
    applyModeToRows();
    updateChrome();
    announceMode();
  }

  function exit() {
    if (!active) return;
    active = false;
    selected = Object.create(null);

    var a = api();
    var inner = a && a.inner();
    if (inner) inner.removeAttribute('data-select-mode');
    applyModeToRows();
    updateChrome();
    announceMode();
  }

  window.PmRequestsSelect = {
    isActive: function () {
      return active;
    },

    enter: enter,
    exit: exit,

    toggle: function () {
      if (active) {
        exit();
      } else {
        enter();
      }
    },

    toggleRow: function (row) {
      if (!active || !row) return;
      var id = row.getAttribute('data-thread-id');
      if (!id) return;
      if (selected[id]) {
        delete selected[id];
      } else {
        selected[id] = true;
      }
      applyModeToRow(row);
      updateChrome();
    },

    selectedIds: selectedIds,

    /**
     * Delete every selected request through the shared undo window. The
     * window opens before the mode closes, so the hold that keeps a server
     * refresh off the rendered rows never lifts between the two.
     */
    deleteSelected: function (fromKeyboard) {
      var a = api();
      if (!active || !a) return;
      var ids = selectedIds();
      if (!ids.length) return;

      // Reset the rows before they leave. Undo puts them back into a list that
      // is out of select mode, and a row still carrying the mode's attributes
      // would come back checked and unswipeable.
      selected = Object.create(null);
      for (var i = 0; i < ids.length; i++) {
        var row = a.row(ids[i]);
        if (row) resetRow(row);
      }

      // The rows above are already reset and select mode is about to close, so
      // a schedule that opens no window has to say so. Silence here reads as a
      // delete that worked.
      var started = window.PmRequestsUndo
        ? window.PmRequestsUndo.scheduleBulk(ids, fromKeyboard)
        : false;
      if (!started && typeof a.actionError === 'function') a.actionError();
      exit();
    }
  };

  function watchAppendedRows() {
    var a = api();
    if (!a || typeof a.onRowsAppended !== 'function') return;
    a.onRowsAppended(function (rows) {
      if (!active) return;
      for (var i = 0; i < rows.length; i++) applyModeToRow(rows[i]);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchAppendedRows);
  } else {
    watchAppendedRows();
  }
})();
